import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaEnvelope, 
  FaApple, 
  FaChevronDown,
  FaBug,
  FaLightbulb,
  FaBook,
  FaHeadset
} from 'react-icons/fa';
import './Support.css';

const Support = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          question: "How do I start my first skincare routine?",
          answer: "Open DailySkin, tap the '+' button, select your routine type (morning or evening), add your skincare products, set reminder times, and start tracking. Your streak counter begins immediately!"
        },
        {
          question: "Can I track multiple skincare routines?",
          answer: "Yes! DailySkin supports unlimited routine tracking. You can create and track morning routines, evening routines, weekly treatments, and special occasion skincare regimens."
        },
        {
          question: "How do I customize my routine cards?",
          answer: "Tap on any routine card, then tap 'Edit'. You can change products, update routine names, modify reminder times, or add progress photos to track your skincare journey."
        }
      ]
    },
    {
      category: "Features & Functions",
      questions: [
        {
          question: "What are Smart Reminders?",
          answer: "Smart Reminders help you maintain consistent skincare habits by sending notifications for your morning routine, evening routine, weekly treatments, and product reorder reminders. You can customize when these reminders appear."
        },
        {
          question: "How does iCloud Sync work?",
          answer: "When enabled, iCloud Sync automatically backs up all your routines and progress data, keeping them synchronized across all your Apple devices. Start your routine on your iPhone and check your progress on your iPad seamlessly."
        },
        {
          question: "Can I export my skincare data?",
          answer: "Yes! Go to Settings > Data Management to export your routine history, progress photos, achievement data, or create backups of your skincare journey."
        },
        {
          question: "What routine types are available?",
          answer: "DailySkin supports morning routines 🌅, evening routines 🌙, and weekly treatments ✨. Each routine type displays relevant icons and can help you set appropriate reminders."
        }
      ]
    },
    {
      category: "Troubleshooting",
      questions: [
        {
          question: "My streak counter isn't updating correctly",
          answer: "Make sure your device's date and time are set correctly. If the issue persists, try force-closing the app and reopening it. The app tracks your daily routine completion automatically."
        },
        {
          question: "I'm not receiving reminder notifications",
          answer: "Check that notifications are enabled for DailySkin in your device Settings > Notifications. Also ensure you've set up reminders for your specific routines in the app."
        },
        {
          question: "My progress photos aren't syncing between devices",
          answer: "Verify that iCloud Sync is enabled in DailySkin settings and that you're signed into the same iCloud account on all devices. Large photos may take a few minutes to sync."
        },
        {
          question: "The app is running slowly",
          answer: "Try restarting the app or your device. If you have extensive routine history (50+ entries), consider archiving old data to improve performance. Make sure you're running the latest version of DailySkin."
        }
      ]
    },
    {
      category: "Account & Privacy",
      questions: [
        {
          question: "Do I need to create an account?",
          answer: "No account needed! DailySkin works entirely with your Apple ID and iCloud for syncing. Your data stays private and secure within Apple's ecosystem."
        },
        {
          question: "Is my skincare data secure?",
          answer: "Absolutely. All your routine data is stored locally on your device and securely synced through iCloud. We don't collect, store, or have access to your personal skincare information."
        },
        {
          question: "Can I use DailySkin offline?",
          answer: "Yes! All your routines and progress tracking work completely offline. You only need an internet connection for iCloud syncing and downloading the app."
        }
      ]
    }
  ];

  const toggleFaq = (categoryIndex, questionIndex) => {
    const faqId = `${categoryIndex}-${questionIndex}`;
    setOpenFaq(openFaq === faqId ? null : faqId);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="support-page">
      <div className="container">
        {/* Hero Section */}
        <motion.section 
          className="support-hero"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <div className="support-hero-content">
            <h1 className="support-title">
              How can we help you?
            </h1>
            <p className="support-subtitle">
              Get support for DailySkin, find answers to common questions, or get in touch with our team.
            </p>
          </div>
        </motion.section>

        {/* Quick Help Cards */}
        <motion.section 
          className="quick-help"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div className="help-card" variants={itemVariants}>
            <div className="help-icon">
              <FaBook />
            </div>
            <h3>User Guide</h3>
            <p>Learn how to make the most of DailySkin with our comprehensive user guide.</p>
            <a href="#faq" className="help-link">View Guide</a>
          </motion.div>

          <motion.div className="help-card" variants={itemVariants}>
            <div className="help-icon">
              <FaBug />
            </div>
            <h3>Report a Bug</h3>
            <p>Found an issue? Let us know and we'll fix it in the next update.</p>
            <a href="#contact" className="help-link">Report Bug</a>
          </motion.div>

          <motion.div className="help-card" variants={itemVariants}>
            <div className="help-icon">
              <FaLightbulb />
            </div>
            <h3>Feature Request</h3>
            <p>Have an idea for DailySkin? We'd love to hear your suggestions!</p>
            <a href="#contact" className="help-link">Suggest Feature</a>
          </motion.div>

          <motion.div className="help-card" variants={itemVariants}>
            <div className="help-icon">
              <FaHeadset />
            </div>
            <h3>Contact Support</h3>
            <p>Need personal assistance? Our support team is here to help.</p>
            <a href="#contact" className="help-link">Get Support</a>
          </motion.div>
        </motion.section>

        {/* FAQ Section */}
        <motion.section 
          id="faq"
          className="faq-section"
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Frequently Asked Questions</h2>
          
          {faqs.map((category, categoryIndex) => (
            <div key={categoryIndex} className="faq-category">
              <h3 className="category-title">{category.category}</h3>
              <div className="faq-list">
                {category.questions.map((faq, questionIndex) => {
                  const isOpen = openFaq === `${categoryIndex}-${questionIndex}`;
                  return (
                    <motion.div 
                      key={questionIndex} 
                      className="faq-item"
                      layout
                    >
                      <button
                        className="faq-question"
                        onClick={() => toggleFaq(categoryIndex, questionIndex)}
                        aria-expanded={isOpen}
                      >
                        <span>{faq.question}</span>
                        <motion.div
                          animate={{ rotate: isOpen ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <FaChevronDown />
                        </motion.div>
                      </button>
                      <motion.div
                        className="faq-answer"
                        initial={false}
                        animate={{
                          height: isOpen ? 'auto' : 0,
                          opacity: isOpen ? 1 : 0
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="faq-answer-content">
                          <p>{faq.answer}</p>
                        </div>
                      </motion.div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </motion.section>

        {/* Contact Section */}
        <motion.section 
          id="contact"
          className="contact-section"
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Still need help?</h2>
          <p className="contact-subtitle">
            Can't find what you're looking for? Get in touch with our support team.
          </p>

          <div className="contact-methods">
            <motion.div 
              className="contact-card"
              whileHover={{ y: -5, scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <div className="contact-icon">
                <FaEnvelope />
              </div>
              <h3>Email Support</h3>
              <p>Get detailed help via email. We typically respond within 24 hours.</p>
              <a href="mailto:support@dailyskin-app.com" className="contact-link">
                support@dailyskin-app.com
              </a>
            </motion.div>

            <motion.div 
              className="contact-card"
              whileHover={{ y: -5, scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <div className="contact-icon">
                <FaApple />
              </div>
              <h3>App Store Reviews</h3>
              <p>Leave feedback directly on the App Store to help other skincare enthusiasts.</p>
              <a href="https://apps.apple.com/app/dailyskin-skincare-gamification/id1234567890" target="_blank" rel="noopener noreferrer" className="contact-link">
                Rate DailySkin
              </a>
            </motion.div>
          </div>

          <motion.div 
            className="support-hours"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h4>Support Hours</h4>
            <p>Monday - Friday: 9:00 AM - 6:00 PM (PST)</p>
            <p>Saturday - Sunday: 10:00 AM - 4:00 PM (PST)</p>
            <small>We aim to respond to all emails within 24 hours during business days.</small>
          </motion.div>
        </motion.section>

        {/* System Requirements */}
        <motion.section 
          className="requirements-section"
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">System Requirements</h2>
          <div className="requirements-grid">
            <div className="requirement-item">
              <h4>iOS Version</h4>
              <p>Requires iOS 14.0 or later</p>
            </div>
            <div className="requirement-item">
              <h4>Compatibility</h4>
              <p>iPhone, iPad, and iPod touch</p>
            </div>
            <div className="requirement-item">
              <h4>Storage</h4>
              <p>50 MB of available space</p>
            </div>
            <div className="requirement-item">
              <h4>Languages</h4>
              <p>English, Spanish, French, German</p>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default Support;
